import React, { type ReactNode, useState, useRef, useMemo } from "react";
import ReactSlider from "react-slider";
import { twMerge, twJoin } from "tailwind-merge";

import { Grabber } from "./components/Grabber";
import { GrabberWithValue } from "./components/GrabberWithValue";
import { Track } from "./components/Track";
import { Mark } from "./components/Mark";
import { getIsMarkActive, getIsTrackActive, defaultValueFormatter, isEqual, getDefaultMarks } from "./utils";
import { MaxButton } from "./components/MaxButton";

interface SliderProps {
  className?: string;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  displayMaxButton?: boolean;
  defaultValue?: number | number[];
  value?: number | number[];
  marks?: number | number[] | false;
  grabberType?: "value" | "default";
  pushable?: boolean;
  onChange?: (value: number | number[], thumbIndex: number) => void;
  onAfterChange?: (value: number | number[], thumbIndex: number) => void;
  onBeforeChange?: (value: number | number[], thumbIndex: number) => void;
  onSliderClick?: (value: number) => void;
  formatDisplayValue?: (value: number, options?: { min: number; max: number }) => ReactNode;
}

export function SupSlider({
  disabled,
  className,
  grabberType = "default",
  min = 0,
  max = 100,
  pushable = false,
  displayMaxButton = true,
  formatDisplayValue = defaultValueFormatter,
  onAfterChange,
  onBeforeChange,
  step = 1,
  ...restProps
}: SliderProps) {
  const sliderRef = useRef<ReactSlider<number | number[]>>(null);
  const [active, setActive] = useState(false);

  const marks = useMemo(() => (grabberType === "value" ? getDefaultMarks(min, max) : false), [grabberType, min, max]);

  const handleBeforeChange = (value: number | number[], thumbIndex: number) => {
    if (disabled) return;

    onBeforeChange?.(value, thumbIndex);
    setActive(true);
  };

  const handleAfterChange = (value: number | number[], thumbIndex: number) => {
    if (disabled) return;

    onAfterChange?.(value, thumbIndex);
    setActive(false);
  };

  return (
    <div className={twMerge("px-8 bg-neutral-50 border-0.5 border-neutral-300", className)}>
      <ReactSlider
        {...restProps}
        ref={sliderRef}
        disabled={disabled}
        pearling={pushable}
        min={min}
        max={max}
        marks={restProps.marks ?? marks}
        step={step}
        className={twJoin(
          "sup-slider group/slider h-10 flex items-center transition-colors duration-200",
          disabled ? "opacity-44 cursor-default" : "active cursor-pointer",
          active && "dragging"
        )}
        trackClassName="sup-slider-track"
        thumbClassName="sup-slider-thumb"
        thumbActiveClassName="sup-slider-thumb-active"
        markClassName="sup-slider-mark"
        onBeforeChange={handleBeforeChange}
        onAfterChange={handleAfterChange}
        renderMark={({ key, style, className, onClick, onKeyDown, role, tabIndex, ...props }) => {
          // @ts-expect-error: For some reason @types/react-slider doesn't cover all available component's methods
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call
          const active = getIsMarkActive(key, sliderRef.current?.getValue());

          return !isEqual(key as number, max, { epsilon: step }) || !displayMaxButton ? (
            <Mark
              key={key}
              active={active}
              style={style}
              className={className}
              onClick={onClick}
              onKeyDown={onKeyDown}
              role={role}
              tabIndex={tabIndex}
              aria-label={props["aria-label"]}
              aria-orientation={props["aria-orientation"]}
              aria-valuemax={props["aria-valuemax"]}
              aria-valuemin={props["aria-valuemin"]}
              aria-valuenow={props["aria-valuenow"]}
              aria-valuetext={props["aria-valuetext"]}
            />
          ) : (
            <MaxButton
              disabled={disabled}
              key={key}
              visible={!active}
              style={style}
              className={className}
              onClick={onClick}
              onKeyDown={onKeyDown}
              role={role}
              tabIndex={tabIndex}
              aria-label={props["aria-label"]}
              aria-orientation={props["aria-orientation"]}
              aria-valuemax={props["aria-valuemax"]}
              aria-valuemin={props["aria-valuemin"]}
              aria-valuenow={props["aria-valuenow"]}
              aria-valuetext={props["aria-valuetext"]}
            />
          );
        }}
        renderTrack={({ className, style, onClick, onKeyDown, role, ...props }, { value, index }) => (
          <Track className={className} active={getIsTrackActive(index, value)} style={style} onClick={onClick} onKeyDown={onKeyDown} role={role} />
        )}
        renderThumb={({ className, style, onClick, onKeyDown, role, tabIndex, ...props }, { valueNow }) =>
          grabberType === "value" ? (
            <GrabberWithValue
              epsilon={step}
              disabled={disabled}
              currentValue={valueNow}
              minValue={min}
              maxValue={max}
              displayMax={displayMaxButton}
              formatter={formatDisplayValue}
              className={twJoin(className, "-translate-y-1/2 -translate-x-1/2")}
              style={style}
              onClick={onClick}
              onKeyDown={onKeyDown}
              role={role}
              tabIndex={tabIndex}
              aria-label={props["aria-label"]}
              aria-orientation={props["aria-orientation"]}
              aria-valuemax={props["aria-valuemax"]}
              aria-valuemin={props["aria-valuemin"]}
              aria-valuenow={props["aria-valuenow"]}
              aria-valuetext={props["aria-valuetext"]}
            />
          ) : (
            <Grabber
              disabled={disabled}
              className={className}
              style={style}
              onClick={onClick}
              onKeyDown={onKeyDown}
              role={role}
              tabIndex={tabIndex}
              aria-label={props["aria-label"]}
              aria-orientation={props["aria-orientation"]}
              aria-valuemax={props["aria-valuemax"]}
              aria-valuemin={props["aria-valuemin"]}
              aria-valuenow={props["aria-valuenow"]}
              aria-valuetext={props["aria-valuetext"]}
            />
          )
        }
      />
    </div>
  );
}
